import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { ChartBarIcon } from '@heroicons/react/24/outline';

/**
 * ProjectComparisonChart - Compare metrics across projects
 */
const ProjectComparisonChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="h-4 bg-gray-200 rounded w-48 mb-4 animate-pulse"></div>
        <div className="h-64 bg-gray-100 rounded animate-pulse"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
          <ChartBarIcon className="w-4 h-4" />
          Project Comparison
        </h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No project data available
        </div>
      </div>
    );
  }

  // Prepare chart data
  // API returns { id, name, domain, requests, errors, errorRate, avgResponseTime, ... }
  const chartData = data.map(project => ({
    project: project.name || formatProjectName(project.id || project.project),
    requests: project.requests || project.totalRequests || 0,
    errors: project.errors || project.errorCount || 0,
    avgResponse: Math.round((project.avgResponseTime || 0) * 1000)
  }));

  // Calculate totals for summary
  const totals = chartData.reduce((acc, item) => ({
    requests: acc.requests + item.requests,
    errors: acc.errors + item.errors
  }), { requests: 0, errors: 0 });

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <ChartBarIcon className="w-4 h-4" />
          Project Comparison
        </h3>
        <span className="text-xs text-gray-500">
          {data.length} projects
        </span>
      </div>

      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="project"
              tick={{ fontSize: 11 }}
              stroke="#9ca3af"
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 11 }}
              stroke="#9ca3af"
              tickFormatter={(value) => value.toLocaleString()}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 11 }}
              stroke="#9ca3af"
              tickFormatter={(value) => `${value}ms`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '12px'
              }}
              formatter={(value, name) => {
                if (name === 'avgResponse') return [`${value}ms`, 'Avg Response'];
                return [value.toLocaleString(), name.charAt(0).toUpperCase() + name.slice(1)];
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: '12px' }}
              formatter={(value) => {
                const labels = {
                  requests: 'Total Requests',
                  errors: 'Errors',
                  avgResponse: 'Avg Response (ms)'
                };
                return labels[value] || value;
              }}
            />
            <Bar
              yAxisId="left"
              dataKey="requests"
              fill="#3b82f6"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              yAxisId="left"
              dataKey="errors"
              fill="#ef4444"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              yAxisId="right"
              dataKey="avgResponse"
              fill="#8b5cf6"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Project summary cards */}
      <div className="mt-4 grid grid-cols-3 gap-2">
        {chartData.map((project, index) => (
          <ProjectCard key={index} project={project} total={totals.requests} />
        ))}
      </div>
    </div>
  );
};

/**
 * Format project name for display
 */
function formatProjectName(name) {
  if (!name) return 'Unknown';

  const nameMap = {
    'iot-dashboard': 'IoT Dashboard',
    'user-dashboard': 'User Dashboard',
    'marketing': 'Marketing Site'
  };

  return nameMap[name] || name
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Project summary card
 */
const ProjectCard = ({ project, total }) => {
  const percentage = total > 0 ? ((project.requests / total) * 100).toFixed(1) : 0;
  const errorRate = project.requests > 0
    ? ((project.errors / project.requests) * 100).toFixed(1)
    : 0;

  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="text-xs font-medium text-gray-700 truncate" title={project.project}>
        {project.project}
      </div>
      <div className="mt-2 space-y-1">
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">Requests</span>
          <span className="text-gray-700 font-medium">
            {project.requests.toLocaleString()}
          </span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">Error Rate</span>
          <span className={`font-medium ${errorRate > 5 ? 'text-red-600' : 'text-green-600'}`}>
            {errorRate}%
          </span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">Avg Response</span>
          <span className={`font-medium ${project.avgResponse > 500 ? 'text-yellow-600' : 'text-green-600'}`}>
            {project.avgResponse}ms
          </span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">Traffic Share</span>
          <span className="text-gray-700">{percentage}%</span>
        </div>
      </div>
    </div>
  );
};

export default ProjectComparisonChart;
